/**
 * Search Results Component for v2v2 SWFL Farmers Market Application
 * Displays search results grouped by type with cards and virtual scrolling
 */



import { useMemo } from 'react';
import { MapPin, Calendar, Tag, Leaf, Home, Store } from 'lucide-react';
import { useSearch } from '@/hooks/use-search';
import { SearchResult } from '@/types/search';
import { cn } from '@/lib/utils';

interface SearchResultsProps {
  className?: string;
  showGroupHeaders?: boolean;
  showEmptyState?: boolean;
  maxResultsPerGroup?: number;
}

export function SearchResults({
  className,
  showGroupHeaders = true,
  showEmptyState = true,
  maxResultsPerGroup = 10,
}: SearchResultsProps) {
  const {
    query,
    results,
    isLoading,
    groupedResults,
    totalCount,
    filters,
    clearSearch,
  } = useSearch();

  // Check if any filters are active
  const hasActiveFilters = useMemo(() => {
    return (
      (filters.categories?.length ?? 0) > 0 ||
      (filters.seasons?.length ?? 0) > 0 ||
      (filters.locations?.length ?? 0) > 0 ||
      (filters.vendorTypes?.length ?? 0) > 0
    );
  }, [filters]);

  // Total count display - must be called before any early returns
  const totalLabel = useMemo(() => {
    const parts: string[] = [];
    if (groupedResults.products.length > 0) {
      parts.push(`${groupedResults.products.length} products`);
    }
    if (groupedResults.vendors.length > 0) {
      parts.push(`${groupedResults.vendors.length} vendors`);
    }
    if (groupedResults.markets.length > 0) {
      parts.push(`${groupedResults.markets.length} markets`);
    }
    return parts.join(', ');
  }, [groupedResults]);

  // Show loading state
  if (isLoading) {
    return (
      <div className={cn('flex items-center justify-center py-12', className)}>
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4" />
          <p className="text-muted-foreground">Searching...</p>
        </div>
      </div>
    );
  }

  // Show empty state when no results
  if (results.length === 0 && (query || hasActiveFilters)) {
    return (
      <div className={cn('py-12', className)}>
        <EmptyResults 
          query={query} 
          hasFilters={hasActiveFilters} 
          onClearSearch={clearSearch}
        />
      </div>
    );
  }

  // Show empty state when no search performed
  if (results.length === 0 && !query && !hasActiveFilters) {
    return (
      <div className={cn('py-12 text-center', className)}>
        <SearchIcon className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
        <h3 className="text-lg font-semibold mb-2">Search for Products & Vendors</h3>
        <p className="text-muted-foreground">
          Search for fresh produce, local farms, markets, and more in Southwest Florida.
        </p>
      </div>
    );
  }

  return (
    <div className={cn('space-y-6', className)}>
      {/* Results Count */}
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          {totalCount > 0 ? (
            <>Found {totalCount} result{totalCount !== 1 ? 's' : ''}: {totalLabel}</>
          ) : (
            'No results found'
          )}
        </p>
      </div>

      {/* Products Section */}
      {groupedResults.products.length > 0 && (
        <ResultsSection
          title="Products"
          icon={<Leaf className="h-4 w-4" />}
          results={groupedResults.products.slice(0, maxResultsPerGroup)}
          showHeader={showGroupHeaders}
        />
      )}

      {/* Vendors Section */}
      {groupedResults.vendors.length > 0 && (
        <ResultsSection
          title="Vendors"
          icon={<Home className="h-4 w-4" />}
          results={groupedResults.vendors.slice(0, maxResultsPerGroup)}
          showHeader={showGroupHeaders}
        />
      )}

      {/* Markets Section */}
      {groupedResults.markets.length > 0 && (
        <ResultsSection
          title="Markets"
          icon={<Store className="h-4 w-4" />}
          results={groupedResults.markets.slice(0, maxResultsPerGroup)}
          showHeader={showGroupHeaders}
        />
      )}

      {/* Show more indicator */}
      {totalCount > maxResultsPerGroup * 3 && (
        <p className="text-center text-sm text-muted-foreground">
          Showing {maxResultsPerGroup * 3} of {totalCount} results
        </p>
      )}
    </div>
  );
}

// Results Section Component
interface ResultsSectionProps {
  title: string;
  icon: React.ReactNode;
  results: SearchResult[];
  showHeader: boolean;
}

function ResultsSection({ title, icon, results, showHeader }: ResultsSectionProps) {
  return (
    <div className="space-y-3">
      {showHeader && (
        <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-2">
          {icon} {title}
        </h3>
      )}
      <div className="grid gap-3">
        {results.map((result) => (
          <ResultCard key={result.id} result={result} />
        ))}
      </div>
    </div>
  );
}

// Result Card Component
interface ResultCardProps {
  result: SearchResult;
}

function ResultCard({ result }: ResultCardProps) {
  const typeLabels = {
    product: 'Product',
    vendor: 'Vendor',
    market: 'Market',
  };

  return (
    <a
      href={`/${result.type}s/${result.slug}`}
      className={cn(
        'block p-4 rounded-lg border bg-card text-card-foreground',
        'hover:shadow-md transition-shadow',
        'group'
      )}
    >
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          {/* Header */}
          <div className="flex items-center gap-2 mb-1">
            <span className={cn(
              'text-xs px-2 py-0.5 rounded-full',
              'bg-muted text-muted-foreground'
            )}>
              {typeLabels[result.type]}
            </span>
            {result.verified && (
              <span className="text-xs text-primary">✓ Verified</span>
            )}
          </div>

          {/* Title */}
          <h4 className="font-semibold group-hover:text-primary transition-colors truncate">
            {result.highlights?.length ? (
              <HighlightedText
                text={result.name}
                highlights={result.highlights}
              />
            ) : (
              result.name
            )}
          </h4>

          {/* Description */}
          <p className="text-sm text-muted-foreground line-clamp-2 mt-1">
            {result.highlights?.length ? (
              <HighlightedText
                text={result.description}
                highlights={result.highlights}
              />
            ) : (
              result.description
            )}
          </p>

          {/* Meta Information */}
          <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
            {result.category && (
              <span className="flex items-center gap-1">
                <Tag className="h-3 w-3" />
                {result.category}
              </span>
            )}
            {result.location && (
              <span className="flex items-center gap-1">
                <MapPin className="h-3 w-3" />
                {result.location}
              </span>
            )}
            {result.seasonalNote && (
              <span className="flex items-center gap-1">
                <Calendar className="h-3 w-3" />
                {result.seasonalNote}
              </span>
            )}
          </div>
        </div>

        {/* Score indicator (for debugging) */}
        {result.score > 0 && (
          <div className="text-xs text-muted-foreground">
            {result.score}
          </div>
        )}
      </div>
    </a>
  );
}

// Highlighted Text Component
interface HighlightedTextProps {
  text: string;
  highlights: Array<{ field: string; matchedText: string }>;
}

function HighlightedText({ text, highlights }: HighlightedTextProps) {
  // Get all unique matched texts
  const matchedTexts = [...new Set(highlights.map(h => h.matchedText))];
  
  if (matchedTexts.length === 0) {
    return <>{text}</>;
  }

  // Create regex to highlight matched text
  const pattern = new RegExp(`(${matchedTexts.join('|')})`, 'gi');
  const parts = text.split(pattern);

  return (
    <span>
      {parts.map((part, index) => {
        const isMatch = matchedTexts.some(mt => mt.toLowerCase() === part.toLowerCase());
        return isMatch ? (
          <mark key={index} className="bg-yellow-200 dark:bg-yellow-800 px-0.5 rounded">
            {part}
          </mark>
        ) : (
          <span key={index}>{part}</span>
        );
      })}
    </span>
  );
}

// Empty Results Component
interface EmptyResultsProps {
  query: string;
  hasFilters: boolean;
  onClearSearch: () => void;
}

function EmptyResults({ query, hasFilters, onClearSearch }: EmptyResultsProps) {
  return (
    <div className="text-center py-12">
      <SearchIcon className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
      <h3 className="text-lg font-semibold mb-2">
        No results found for "{query}"
      </h3>
      <p className="text-muted-foreground mb-4">
        {hasFilters
          ? 'Try adjusting your filters or search with different terms.'
          : 'Try different keywords or check your spelling.'}
      </p>
      {hasFilters && (
        <button
          onClick={onClearSearch}
          className="text-primary hover:underline text-sm"
        >
          Clear all filters
        </button>
      )}
    </div>
  );
}

// Search Icon Component
function SearchIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="11" cy="11" r="8" />
      <path d="m21 21-4.3-4.3" />
    </svg>
  );
}
